import { computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { defineStore } from 'pinia'
import { nl as prime_nl } from 'primelocale/js/nl.js'
import { de as prime_de } from 'primelocale/js/de.js'
import { en as prime_en } from 'primelocale/js/en.js'
import { usePrimeVue } from 'primevue/config'
import * as z from 'zod'
import { nl as zod_nl, de as zod_de, en as zod_en } from 'zod/locales'

import { useUser } from '@/composables/useUser'

export const useLocaleStore = defineStore('locale', () => {
  const { availableLocales, locale, t } = useI18n({ useScope: 'global' })
  const primevue = usePrimeVue()

  // primevue locales are available at https://github.com/primefaces/primelocale
  const primevueLocales = { nl: prime_nl, de: prime_de, en: prime_en }

  // zod locales are available at https://zod.dev/error-customization#locales
  const zodLocales = { nl: zod_nl, de: zod_de, en: zod_en }

  const { user, setLanguage: setUserLanguage } = useUser()
  const preferredLanguage = computed(() => user.value?.preferredLanguage || locale.value)

  watchEffect(() => {
    if (preferredLanguage.value) {
      locale.value = preferredLanguage.value
    } else {
      const browserLang = navigator.language.slice(0, 2)
      const lang = availableLocales.includes(browserLang) ? browserLang : 'en'
      locale.value = lang
    }
    // Update PrimeVue components locale based on the current locale
    // example components: Calendar, Upload etc.
    primevue.config.locale = primevueLocales[locale.value]

    // Update Zod locale for validation messages with custom error handler
    const zodConfig = zodLocales[locale.value]()
    zodConfig.customError = (issue) => {
      const baseKey = `zod.${issue.code}`

      switch (issue.code) {
        case 'invalid_type':
          return t(baseKey)
        case 'too_big':
          return t(baseKey, { maximum: issue.maximum })
        case 'too_small':
          return t(baseKey, { minimum: issue.minimum })
        case 'not_multiple_of':
          return t(baseKey, { divisor: issue.divisor })
        case 'unrecognized_keys':
          return t(baseKey)
        case 'invalid_union':
          return t(baseKey)
        case 'invalid_key':
          return t(baseKey)
        case 'invalid_element':
          return t(baseKey)
        case 'invalid_value':
          return t(baseKey)
        case 'invalid_format':
          return t(baseKey)
        case 'custom':
          return t(baseKey)
        default:
          return t('zod.custom')
      }
    }
    z.config(zodConfig)
  })

  const setLanguage = async (code) => {
    await setUserLanguage(code)
    // is pickup by above watch on the user.
  }

  return { lang: locale, setLanguage }
})
